import { useEffect, useState, useMemo, useRef } from "react";
import BotonFlotante from "../../components/BotonFlotante";
import BarraSuperior from "../barra_superior";
import { consultarSimple } from "../../../functions/crud";
import { consultarConCache } from "../../../functions/consultarConCache";
import { crearTitulo, crearGrafica } from "../../../functions/gestion";
import {
    Chart, RadialLinearScale, PointElement, LineElement, LineController, Filler,
    Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, ArcElement
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

Chart.register(
    RadialLinearScale, PointElement, LineElement, LineController, Filler,
    Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, ArcElement,
    ChartDataLabels
);

const CHART_CONFIG = { alto: 600, ancho: 1300 };
const MODULOS_OCULTOS_RADAR = ["Usuarios", "Clientes", "Rondas", "Tickets", "Otros"];
const PERMISOS_DISPONIBLES = ["empleado", "guarda", "residente", "administrador", "super"];
const NITS_EXCLUIDOS = ["900362633", "123456"];

const REGEX_MODULOS = [
    { nombre: "Noticias", test: /^\/?coordi_ph\/\d+\/noticias\// },
    { nombre: "Visitantes", test: /^\/?coordi_ph\/\d+\/(visitantes|historico|eventos)/ },
    { nombre: "Domicilios", test: /^\/?coordi_ph\/\d+\/domicilios/ },
    { nombre: "Reservas", test: /^\/?coordi_ph_clientes\/\d+\/reservas/ },
    { nombre: "Reservas", test: /^\/?coordi_ph_clientes\/\d+\/reserva_zona/ },
    { nombre: "Reservas", test: /^\/?coordi_ph_clientes\/\d+\/reserva_mes/ },
    { nombre: "Reservas", test: /^\/?coordi_ph_clientes\/\d+\/aprobados/ },
    { nombre: "Reservas", test: /^\/?coordi_ph\/\d+\/pqrs/ },
    { nombre: "Reservas", test:  /^\/?reservas\// },
    { nombre: "Reservas", test: /^\/?coordi_ph_clientes\/\d+\/.*\/reservas/ },
    { nombre: "Anuncios", test: /^\/?coordi_ph\/\d+\/anuncios/ },
    { nombre: "Notificaciones", test: /^\/?coordi_ph_clientes\/\d+\/fcm/ },
    { nombre: "Novedades", test: /^\/?coordi_ph\/\d+\/novedad/ },
    { nombre: "Rondas", test: /^\/?(?:rondas|rastreo)\// },
    { nombre: "Eventos", test: /^\/?coordi_ph\/\d+\/eventos/ },
    { nombre: "Tickets", test: /^\/?tickets\// },
    { nombre: "Usuarios", test: /^\/?(?:usuarios|autorizados|pendientes|correos)\// },
    { nombre: "Clientes", test: /^\/?(?:clientes|legal|informe|coordi_ph_clientes\/\d+\/-)/ },
    { nombre: "Censo", test: /^\/?coordi_ph\/\d+\/censo/ },
    { nombre: "Documentos", test: /^\/?coordi_ph\/\d+\/documentos/ },
    { nombre: "Chats", test: /^\/?coordi_ph\/\d+\/chat/ },
    { nombre: "Tutoriales", test: /^\/?tutoriales\// },
    { nombre: "Noticias", test: /^\/?(?:calificacion|version|noticias)\// },
    { nombre: "Logs", test: /^\/?logs\// },
    { nombre: "Domicilios", test: /^\/?notificaciones(?:_excluidas)?\// },
    { nombre: "SOS", test: /^\/?sos\// },
    { nombre: "Fiesta", test: /^\/?fiesta\// },
    { nombre: "Ordenes", test: /^\/?ordenes\// },
    { nombre: "Notificaciones", test: /^\/?integracion\// },
];

const REGEX_REGISTRO_USUARIO = /^\/?pendientes\/\d+\/[^/]+$/;

const esRegistroUsuario = (log) => {
    if (!log) return false;
    const accion = String(log.accion || "").toLowerCase().trim();
    const url = String(log.url || "");
    return (accion === "eliminar") && REGEX_REGISTRO_USUARIO.test(url);
};

const extraerFecha = (log) => {
    if (!log?.fecha) return null;
    const partes = log.fecha.split(" ");
    if (!partes[0]) return null;
    const segmentos = partes[0].split("-");
    return segmentos[2] ? segmentos[2] : null;
};

const SelectorCliente = ({ clientes, clienteSeleccionado, onSeleccionar }) => {
    const [busqueda, setBusqueda] = useState("");
    const [abierto, setAbierto] = useState(false);
    const ref = useRef(null);

    const nombreSeleccionado = clienteSeleccionado || "";

    useEffect(() => {
        setBusqueda(nombreSeleccionado);
    }, [nombreSeleccionado]);

    useEffect(() => {
        const handleClickFuera = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setAbierto(false);
                setBusqueda(nombreSeleccionado);
            }
        };
        document.addEventListener("mousedown", handleClickFuera);
        return () => document.removeEventListener("mousedown", handleClickFuera);
    }, [nombreSeleccionado]);

    const listaFiltrada = useMemo(() => {
        const termino = busqueda.trim().toLowerCase();
        return clientes
            .filter((nombre) => !termino || nombre.toLowerCase().includes(termino))
            .sort((a, b) => a.localeCompare(b));
    }, [clientes, busqueda]);

    const seleccionar = (nombre) => {
        onSeleccionar(nombre);
        setBusqueda(nombre);
        setAbierto(false);
    };

    const limpiar = () => {
        onSeleccionar("");
        setBusqueda("");
        setAbierto(false);
    };

    return (
        <div ref={ref} style={{ position: "relative", minWidth: "340px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                    type="text"
                    value={busqueda}
                    placeholder="Escribir para buscar copropiedad..."
                    onChange={(e) => {
                        setBusqueda(e.target.value);
                        setAbierto(true);
                        if (!e.target.value) onSeleccionar("");
                    }}
                    onFocus={() => setAbierto(true)}
                    style={{
                        padding: "6px 12px",
                        fontSize: "1em",
                        width: "100%",
                        border: "1px solid #b0c4de",
                        borderRadius: 4,
                        outline: "none",
                        boxSizing: "border-box",
                    }}
                />
                {clienteSeleccionado && (
                    <button
                        onClick={limpiar}
                        title="Limpiar selección"
                        style={{ padding: "6px 10px", fontSize: "0.95em", cursor: "pointer", whiteSpace: "nowrap" }}
                    >
                        ✕ Limpiar
                    </button>
                )}
            </div>

            {abierto && (
                <ul
                    style={{
                        position: "absolute",
                        top: "calc(100% + 4px)",
                        left: 0,
                        right: 0,
                        maxHeight: 280,
                        overflowY: "auto",
                        margin: 0,
                        padding: 0,
                        listStyle: "none",
                        backgroundColor: "#fff",
                        border: "1px solid #b0c4de",
                        borderRadius: 4,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                        zIndex: 9999,
                    }}
                >
                    <li
                        onClick={() => { onSeleccionar(""); setBusqueda(""); setAbierto(false); }}
                        style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            color: "#888",
                            borderBottom: "1px solid #eee",
                            fontStyle: "italic",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f0f8ff")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "")}
                    >
                        — Todos los clientes —
                    </li>

                    {listaFiltrada.length > 0 ? (
                        listaFiltrada.map((nombre) => (
                            <li
                                key={nombre}
                                onClick={() => seleccionar(nombre)}
                                style={{
                                    padding: "8px 12px",
                                    cursor: "pointer",
                                    backgroundColor: nombre === clienteSeleccionado ? "#ddeeff" : "",
                                    fontWeight: nombre === clienteSeleccionado ? "bold" : "normal",
                                }}
                                onMouseEnter={(e) => {
                                    if (nombre !== clienteSeleccionado)
                                        e.currentTarget.style.backgroundColor = "#f0f8ff";
                                }}
                                onMouseLeave={(e) => {
                                    if (nombre !== clienteSeleccionado)
                                        e.currentTarget.style.backgroundColor = "";
                                }}
                            >
                                {nombre}
                            </li>
                        ))
                    ) : (
                        <li style={{ padding: "8px 12px", color: "#aaa" }}>Sin resultados</li>
                    )}
                </ul>
            )}
        </div>
    );
};

const EstadisticaLogs = () => {
    const [estadisticas, setEstadisticas] = useState({
        porUnidad: {},
        porDia: {},
        porHora: {},
        acciones: {},
        usuariosActivos: {},
        porDispositivo: {},
        porPermisos: {},
        porModulo: {},
        modulosPorCliente: {},
        moduloPorAccion: {
            leer: {},
            actualizar: {},
            crear: {},
            eliminar: {}
        }
    });
    const [clientes, setClientes] = useState({});
    const [cargando, setCargando] = useState(false);
    const [filtros, setFiltros] = useState({ anio: "", mes: "" });
    const [permisosSeleccionados, setPermisosSeleccionados] = useState(PERMISOS_DISPONIBLES);
    const [clienteSeleccionado, setClienteSeleccionado] = useState("");
    const [datosOriginales, setDatosOriginales] = useState(null);

    const hoy = useMemo(() => {
        const d = new Date();
        return { año: d.getFullYear(), mes: String(d.getMonth() + 1).padStart(2, "0") };
    }, []);

    const detectarModulo = (url) => {
        if (!url) return "Otros";
        for (const { nombre, test } of REGEX_MODULOS) {
            if (test.test(url)) return nombre;
        }
        return "Otros";
    };

    const categorizarAccion = (accion) => {
        if (!accion) return null;
        const accionLower = accion.toLowerCase();
        
        if (accionLower.includes("leer") || accionLower.includes("consultar") || accionLower.includes("ver")) {
            return "leer";
        }
        if (accionLower.includes("actualizar") || accionLower.includes("editar") || accionLower.includes("modificar")) {
            return "actualizar";
        }
        if (accionLower.includes("crear") || accionLower.includes("agregar") || accionLower.includes("añadir")) {
            return "crear";
        }
        if (accionLower.includes("eliminar") || accionLower.includes("borrar") || accionLower.includes("quitar")) {
            return "eliminar";
        }
        
        return null;
    };

    const togglePermiso = (permiso) => {
        setPermisosSeleccionados(prev => 
            prev.includes(permiso)
                ? prev.filter(p => p !== permiso)
                : [...prev, permiso]
        );
    };

    const toggleTodosPermisos = () => {
        setPermisosSeleccionados(prev => 
            prev.length === PERMISOS_DISPONIBLES.length ? [] : [...PERMISOS_DISPONIBLES]
        );
    };

    const registrosUsuariosPorDia = useMemo(() => {
        if (!datosOriginales) return {};
        const conteo = {};
        datosOriginales.forEach(({ log, nit }) => {
            if (NITS_EXCLUIDOS.includes(nit)) return;
            const permisoLog = String(log.permisos || "N/A").toLowerCase().trim();
            if (!permisosSeleccionados.includes(permisoLog)) return;
            if (!esRegistroUsuario(log)) return;
            const dia = extraerFecha(log);
            if (dia) conteo[dia] = (conteo[dia] || 0) + 1;
        });
        return conteo;
    }, [datosOriginales, permisosSeleccionados]);

    const registrosUsuariosPorDiaCliente = useMemo(() => {
        if (!datosOriginales || !clienteSeleccionado) return {};
        const conteo = {};
        datosOriginales.forEach(({ log, nombreCliente, nit }) => {
            if (NITS_EXCLUIDOS.includes(nit)) return;
            if (nombreCliente !== clienteSeleccionado) return;
            const permisoLog = String(log.permisos || "N/A").toLowerCase().trim();
            if (!permisosSeleccionados.includes(permisoLog)) return;
            if (!esRegistroUsuario(log)) return;
            const dia = extraerFecha(log);
            if (dia) conteo[dia] = (conteo[dia] || 0) + 1;
        });
        return conteo;
    }, [datosOriginales, permisosSeleccionados, clienteSeleccionado]);

    const estadisticasFiltradas = useMemo(() => {
        if (!datosOriginales || permisosSeleccionados.length === 0) {
            return {
                porUnidad: {},
                porDia: {},
                porHora: {},
                acciones: {},
                usuariosActivos: {},
                porDispositivo: {},
                porPermisos: {},
                porModulo: {},
                modulosPorCliente: {},
                moduloPorAccion: {
                    leer: {},
                    actualizar: {},
                    crear: {},
                    eliminar: {}
                }
            };
        }
        if (permisosSeleccionados.length === PERMISOS_DISPONIBLES.length) {
            return estadisticas;
        }
        const acumuladores = {
            unidad: {},
            dia: {},
            hora: {},
            acciones: {},
            dispositivo: {},
            permisos: {},
            modulo: {},
            usuarios: {},
            modulosPorCliente: {},
            moduloPorAccion: {
                leer: {},
                actualizar: {},
                crear: {},
                eliminar: {}
            }
        };

        datosOriginales.forEach(({ log, nombreCliente, dia, nit }) => {
            const permisoLog = String(log.permisos || "N/A").toLowerCase().trim();
            if (!permisosSeleccionados.includes(permisoLog) || NITS_EXCLUIDOS.includes(nit)) return;
            if (log.fecha) {
                const h = log.fecha.split(" ")[1]?.split(":")[0];
                if (h) acumuladores.hora[h] = (acumuladores.hora[h] || 0) + 1;
            }
            const disp = String(log.dispositivo || "desconocido").toLowerCase().trim();
            acumuladores.dispositivo[disp] = (acumuladores.dispositivo[disp] || 0) + 1;
            const accion = String(log.accion || "sin_accion");
            acumuladores.acciones[accion] = (acumuladores.acciones[accion] || 0) + 1;
            acumuladores.permisos[permisoLog] = (acumuladores.permisos[permisoLog] || 0) + 1;
            const modulo = detectarModulo(log.url);
            acumuladores.modulo[modulo] = (acumuladores.modulo[modulo] || 0) + 1;
            const categoriaAccion = categorizarAccion(log.accion);
            if (categoriaAccion) {
                acumuladores.moduloPorAccion[categoriaAccion][modulo] = 
                    (acumuladores.moduloPorAccion[categoriaAccion][modulo] || 0) + 1;
            }
            if (dia) {
                acumuladores.dia[dia] = (acumuladores.dia[dia] || 0) + 1;
            }
            if (nombreCliente) {
                acumuladores.unidad[nombreCliente] = (acumuladores.unidad[nombreCliente] || 0) + 1;
            }
            if (log.correo) {
                acumuladores.usuarios[log.correo] = (acumuladores.usuarios[log.correo] || 0) + 1;
            }
            if (nombreCliente) {
                if (!acumuladores.modulosPorCliente[nombreCliente]) {
                    acumuladores.modulosPorCliente[nombreCliente] = {};
                }
                acumuladores.modulosPorCliente[nombreCliente][modulo] = 
                    (acumuladores.modulosPorCliente[nombreCliente][modulo] || 0) + 1;
            }
        });

        return {
            porUnidad: acumuladores.unidad,
            porDia: acumuladores.dia,
            porHora: acumuladores.hora,
            acciones: acumuladores.acciones,
            usuariosActivos: acumuladores.usuarios,
            porDispositivo: acumuladores.dispositivo,
            porPermisos: acumuladores.permisos,
            porModulo: acumuladores.modulo,
            modulosPorCliente: acumuladores.modulosPorCliente,
            moduloPorAccion: acumuladores.moduloPorAccion
        };
    }, [datosOriginales, permisosSeleccionados, estadisticas]);

    const top3Unidades = useMemo(() => 
        Object.entries(estadisticasFiltradas.porUnidad)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 3)
            .map(([unidad]) => unidad),
        [estadisticasFiltradas.porUnidad]
    );

    const particionUnidades = useMemo(() => {
        const top3 = {}, resto = {};
        Object.entries(estadisticasFiltradas.porUnidad).forEach(([k, v]) => {
            (top3Unidades.includes(k) ? top3 : resto)[k] = v;
        });
        return { top3, resto };
    }, [estadisticasFiltradas.porUnidad, top3Unidades]);

    const porModuloFiltrado = useMemo(() =>
        Object.fromEntries(
            Object.entries(estadisticasFiltradas.porModulo).filter(([m]) => !MODULOS_OCULTOS_RADAR.includes(m))
        ),
        [estadisticasFiltradas.porModulo]
    );

    const porModuloSinTop3 = useMemo(() => {
        const modulosSinTop3 = {};
        
        Object.entries(estadisticasFiltradas.modulosPorCliente).forEach(([cliente, modulos]) => {
            if (!top3Unidades.includes(cliente)) {
                Object.entries(modulos).forEach(([modulo, cantidad]) => {
                    modulosSinTop3[modulo] = (modulosSinTop3[modulo] || 0) + cantidad;
                });
            }
        });

        return Object.fromEntries(
            Object.entries(modulosSinTop3).filter(([m]) => !MODULOS_OCULTOS_RADAR.includes(m))
        );
    }, [estadisticasFiltradas.modulosPorCliente, top3Unidades]);

    const moduloPorAccionFiltrado = useMemo(() => ({
        leer: Object.fromEntries(
            Object.entries(estadisticasFiltradas.moduloPorAccion.leer)
                .filter(([m]) => !MODULOS_OCULTOS_RADAR.includes(m))
        ),
        actualizar: Object.fromEntries(
            Object.entries(estadisticasFiltradas.moduloPorAccion.actualizar)
                .filter(([m]) => !MODULOS_OCULTOS_RADAR.includes(m))
        ),
        crear: Object.fromEntries(
            Object.entries(estadisticasFiltradas.moduloPorAccion.crear)
                .filter(([m]) => !MODULOS_OCULTOS_RADAR.includes(m))
        ),
        eliminar: Object.fromEntries(
            Object.entries(estadisticasFiltradas.moduloPorAccion.eliminar)
                .filter(([m]) => !MODULOS_OCULTOS_RADAR.includes(m))
        )
    }), [estadisticasFiltradas.moduloPorAccion]);

    const listaClientesOrdenados = useMemo(() => 
        Object.keys(estadisticasFiltradas.porUnidad).sort((a, b) => a.localeCompare(b)),
        [estadisticasFiltradas.porUnidad]
    );

    useEffect(() => {
        (async () => {
            const lista = await consultarConCache("clientes/", consultarSimple);
            if (!lista) return;

            const clientesFinal = {};
            await Promise.all(
                Object.entries(lista).map(async ([nit, auto]) => {
                    const data = await consultarConCache(`coordi_ph_clientes/${nit}/${auto}`, consultarSimple);
                    if (data?.Estado !== "RECHAZADO") {
                        clientesFinal[nit] = { id: auto, nombre: data.Nombre.toUpperCase() };
                    }
                })
            );
            setClientes(clientesFinal);
        })();
    }, []);

    const procesarLog = (log, acumuladores, nombreCliente = null) => {
        if (!log) return;

        if (log.fecha) {
            const h = log.fecha.split(" ")[1]?.split(":")[0];
            if (h) acumuladores.hora[h] = (acumuladores.hora[h] || 0) + 1;
        }

        const disp = String(log.dispositivo || "desconocido").toLowerCase().trim();
        acumuladores.dispositivo[disp] = (acumuladores.dispositivo[disp] || 0) + 1;

        const accion = String(log.accion || "sin_accion");
        acumuladores.acciones[accion] = (acumuladores.acciones[accion] || 0) + 1;

        const perm = String(log.permisos || "N/A");
        acumuladores.permisos[perm] = (acumuladores.permisos[perm] || 0) + 1;

        const modulo = detectarModulo(log.url);
        acumuladores.modulo[modulo] = (acumuladores.modulo[modulo] || 0) + 1;
        const categoriaAccion = categorizarAccion(log.accion);
        if (categoriaAccion) {
            acumuladores.moduloPorAccion[categoriaAccion][modulo] = 
                (acumuladores.moduloPorAccion[categoriaAccion][modulo] || 0) + 1;
        }

        if (nombreCliente) {
            if (!acumuladores.modulosPorCliente[nombreCliente]) {
                acumuladores.modulosPorCliente[nombreCliente] = {};
            }
            acumuladores.modulosPorCliente[nombreCliente][modulo] = 
                (acumuladores.modulosPorCliente[nombreCliente][modulo] || 0) + 1;
        }
    };

    const consultar = async () => {
        if (!filtros.anio || !filtros.mes) return alert("Debes seleccionar año y mes.");
        setCargando(true);

        const diasMes = new Date(filtros.anio, filtros.mes, 0).getDate();
        const nits = Object.keys(clientes);

        const acumuladores = {
            unidad: {},
            dia: {},
            hora: {},
            acciones: {},
            dispositivo: {},
            permisos: {},
            modulo: {},
            usuarios: {},
            modulosPorCliente: {},
            moduloPorAccion: {
                leer: {},
                actualizar: {},
                crear: {},
                eliminar: {}
            }
        };

        const logsCompletos = [];

        await Promise.all(
            nits.map(async (nit) => {
                if (NITS_EXCLUIDOS.includes(nit)) return;
                
                let totalUnidad = 0;
                const dias = await Promise.all(
                    Array.from({ length: diasMes }, (_, i) => 
                        consultarConCache(
                            `logs/${nit}/${filtros.anio}/${filtros.mes}/${String(i + 1).padStart(2, "0")}`,
                            consultarSimple
                        )
                    )
                );

                dias.forEach((info, i) => {
                    if (!info) return;
                    const dia = String(i + 1).padStart(2, "0");
                    let totalDia = 0;

                    Object.entries(info).forEach(([correo, cantidad]) => {
                        totalDia += cantidad;
                        acumuladores.usuarios[correo] = (acumuladores.usuarios[correo] || 0) + cantidad;
                    });

                    acumuladores.dia[dia] = (acumuladores.dia[dia] || 0) + totalDia;
                    totalUnidad += totalDia;
                });

                acumuladores.unidad[clientes[nit].nombre] = totalUnidad;
            })
        );

        await Promise.all(
            nits.map(async (nit) => {
                if (NITS_EXCLUIDOS.includes(nit)) return;
                
                const nombreCliente = clientes[nit].nombre;
                
                for (let i = 1; i <= diasMes; i++) {
                    const diaFormateado = String(i).padStart(2, "0");
                    const logs = await consultarSimple(
                        `logs/${nit}/${filtros.anio}/${filtros.mes}/${diaFormateado}`
                    );
                    if (!logs) continue;

                    Object.values(logs).forEach(logsUser => {
                        Object.values(logsUser).forEach(log => {
                            procesarLog(log, acumuladores, nombreCliente);
                            logsCompletos.push({ log, nombreCliente, dia: diaFormateado, nit });
                        });
                    });
                }
            })
        );

        setEstadisticas({
            porUnidad: acumuladores.unidad,
            porDia: acumuladores.dia,
            porHora: acumuladores.hora,
            acciones: acumuladores.acciones,
            usuariosActivos: acumuladores.usuarios,
            porDispositivo: acumuladores.dispositivo,
            porPermisos: acumuladores.permisos,
            porModulo: acumuladores.modulo,
            modulosPorCliente: acumuladores.modulosPorCliente,
            moduloPorAccion: acumuladores.moduloPorAccion
        });

        setDatosOriginales(logsCompletos);
        setCargando(false);
    };

    const renderGrafica = (titulo, datos, label) => 
        Object.keys(datos).length > 0 && (
            <>
                {crearTitulo(titulo)}
                {crearGrafica("barra", label, datos, CHART_CONFIG.alto, CHART_CONFIG.ancho, "numero", true, false)}
            </>
        );

    const datosParaExcel = useMemo(() => {
        if (!datosOriginales || datosOriginales.length === 0) return [];

        return datosOriginales
            .filter(({ log, nit }) => {
                const permisoLog = String(log.permisos || "N/A").toLowerCase().trim();
                return permisosSeleccionados.includes(permisoLog) && !NITS_EXCLUIDOS.includes(nit);
            })
            .map(({ log, nombreCliente, dia }) => ({
                Cliente: nombreCliente || "N/A",
                Fecha: log.fecha || "N/A",
                Usuario: log.usuario || "N/A",
                Permiso: log.permisos || "N/A",
                Dispositivo: log.dispositivo || "desconocido",
                Accion: log.accion || "sin_accion",
                Modulo: detectarModulo(log.url),
            }));
    }, [datosOriginales, permisosSeleccionados]);

    const columnasExcel = [
        { label: "Cliente", key: "Cliente" },
        { label: "Fecha Completa", key: "Fecha" },
        { label: "Usuario", key: "Usuario" },
        { label: "Permiso", key: "Permiso" },
        { label: "Dispositivo", key: "Dispositivo" },
        { label: "Acción", key: "Accion" },
        { label: "Módulo", key: "Modulo" },
    ];

    return (
        <div>
            <BarraSuperior />
            {crearTitulo("Consulta de actividad por copropiedad")}

            <div style={{ padding: 20 }}>
                <label>Año: </label>
                <select value={filtros.anio} onChange={(e) => setFiltros({...filtros, anio: e.target.value})}>
                    <option value="">-- Seleccionar --</option>
                    {[0, 1].map(i => {
                        const y = hoy.año - i;
                        return <option key={y} value={y}>{y}</option>;
                    })}
                </select>

                <label style={{ marginLeft: 20 }}>Mes: </label>
                <select value={filtros.mes} onChange={(e) => setFiltros({...filtros, mes: e.target.value})}>
                    <option value="">-- Seleccionar --</option>
                    {Array.from({ length: 12 }, (_, i) => {
                        const m = String(i + 1).padStart(2, "0");
                        return <option key={m} value={m}>{m}</option>;
                    })}
                </select>

                <button onClick={consultar} style={{ marginLeft: 20, padding: "6px 14px" }}>
                    Consultar
                </button>
            </div>

            {datosOriginales && (
                <div style={{ padding: 20, backgroundColor: "#f5f5f5", marginBottom: 20 }}>
                    <div style={{ marginBottom: 10 }}>
                        <strong>Filtrar por permisos:</strong>
                        <button 
                            onClick={toggleTodosPermisos}
                            style={{ marginLeft: 15, padding: "4px 10px", fontSize: "0.9em" }}
                        >
                            {permisosSeleccionados.length === PERMISOS_DISPONIBLES.length 
                                ? "Desmarcar todos" 
                                : "Marcar todos"}
                        </button>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "15px" }}>
                        {PERMISOS_DISPONIBLES.map(permiso => (
                            <label key={permiso} style={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                                <input
                                    type="checkbox"
                                    checked={permisosSeleccionados.includes(permiso)}
                                    onChange={() => togglePermiso(permiso)}
                                    style={{ marginRight: 6, cursor: "pointer" }}
                                />
                                <span style={{ textTransform: "capitalize" }}>{permiso}</span>
                            </label>
                        ))}
                    </div>
                    <div style={{ marginTop: 10, fontSize: "0.9em", color: "#666" }}>
                        {permisosSeleccionados.length === 0 
                            ? "⚠️ Selecciona al menos un permiso para ver las gráficas"
                            : `Mostrando datos de: ${permisosSeleccionados.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(", ")}`
                        }
                    </div>
                </div>
            )}

            {datosOriginales && listaClientesOrdenados.length > 0 && (
                <div style={{
                    padding: 20,
                    backgroundColor: "#f0f8ff",
                    marginBottom: 20,
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 12,
                }}>
                    <label style={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                        Ver estadísticas de cliente específico:
                    </label>
                    <SelectorCliente
                        clientes={listaClientesOrdenados}
                        clienteSeleccionado={clienteSeleccionado}
                        onSeleccionar={setClienteSeleccionado}
                    />
                    {clienteSeleccionado && (
                        <span style={{ color: "#555", fontSize: "0.9em" }}>
                            Mostrando datos de: <strong>{clienteSeleccionado}</strong>
                        </span>
                    )}
                </div>
            )}

            {cargando ? (
                <p>Cargando datos...</p>
            ) : permisosSeleccionados.length === 0 ? (
                <p style={{ padding: 20, textAlign: "center", color: "#999" }}>
                    Selecciona al menos un permiso para visualizar las gráficas
                </p>
            ) : (
                <>
                    {renderGrafica("Actividad por mes por unidad", estadisticasFiltradas.porUnidad, "Usuarios por unidad")}
                    {renderGrafica("Actividad por mes por unidad (Sin top 3)", particionUnidades.resto, "Usuarios por unidad (sin top 3)")}
                    {renderGrafica("Actividad por mes por unidad (Top 3)", particionUnidades.top3, "Usuarios por unidad (top 3)")}
                    {renderGrafica("Actividad por día a nivel general", estadisticasFiltradas.porDia, "Usuarios por día")}
                    {renderGrafica("Actividad por hora a nivel general", estadisticasFiltradas.porHora, "Horas")}
                    {renderGrafica("Uso por dispositivo", estadisticasFiltradas.porDispositivo, "Dispositivo")}
                    {renderGrafica("Acciones más usadas", estadisticasFiltradas.acciones, "Acciones")}
                    {renderGrafica("Acceso por permisos", estadisticasFiltradas.porPermisos, "Permisos")}
                    {renderGrafica("Uso por módulo (General)", porModuloFiltrado, "Módulos")}
                    
                    {renderGrafica("Uso por módulo (Sin top 3 clientes)", porModuloSinTop3, "Módulos (sin top 3)")}

                    {renderGrafica("Uso por módulo - Acción: Leer", moduloPorAccionFiltrado.leer, "Lecturas por módulo")}
                    {renderGrafica("Uso por módulo - Acción: Crear", moduloPorAccionFiltrado.crear, "Creaciones por módulo")}
                    {renderGrafica("Uso por módulo - Acción: Actualizar", moduloPorAccionFiltrado.actualizar, "Actualizaciones por módulo")}
                    {renderGrafica("Uso por módulo - Acción: Eliminar", moduloPorAccionFiltrado.eliminar, "Eliminaciones por módulo")}

                    {renderGrafica("Fecha de registro (General)", registrosUsuariosPorDia, "Registros de usuarios")}
                    
                    {top3Unidades.map(nombreCliente => {
                        const modulosCliente = estadisticasFiltradas.modulosPorCliente[nombreCliente];
                        if (!modulosCliente) return null;
                        
                        const modulosFiltrados = Object.fromEntries(
                            Object.entries(modulosCliente).filter(([m]) => !MODULOS_OCULTOS_RADAR.includes(m))
                        );
                        
                        return Object.keys(modulosFiltrados).length > 0 && (
                            <div key={nombreCliente}>
                                {crearTitulo(`Uso por módulo - ${nombreCliente}`)}
                                {crearGrafica("barra", `Módulos - ${nombreCliente}`, modulosFiltrados, CHART_CONFIG.alto, CHART_CONFIG.ancho, "numero", true, false)}
                            </div>
                        );
                    })}

                    {clienteSeleccionado && estadisticasFiltradas.modulosPorCliente[clienteSeleccionado] && (() => {
                        const modulosCliente = estadisticasFiltradas.modulosPorCliente[clienteSeleccionado];
                        const modulosFiltrados = Object.fromEntries(
                            Object.entries(modulosCliente).filter(([m]) => !MODULOS_OCULTOS_RADAR.includes(m))
                        );
                        
                        return (
                            <>
                                {Object.keys(modulosFiltrados).length > 0 && (
                                    <div>
                                        {crearTitulo(`Uso por módulo - ${clienteSeleccionado}`)}
                                        {crearGrafica("barra", `Módulos - ${clienteSeleccionado}`, modulosFiltrados, CHART_CONFIG.alto, CHART_CONFIG.ancho, "numero", true, false)}
                                    </div>
                                )}

                                {Object.keys(registrosUsuariosPorDiaCliente).length > 0 && (
                                    <div>
                                        {crearTitulo(`Fecha de registro - ${clienteSeleccionado}`)}
                                        {crearGrafica("barra", `Registros - ${clienteSeleccionado}`, registrosUsuariosPorDiaCliente, CHART_CONFIG.alto, CHART_CONFIG.ancho, "numero", true, false)}
                                    </div>
                                )}
                            </>
                        );
                    })()}
                    
                    {renderGrafica("Usuarios más activos", estadisticasFiltradas.usuariosActivos, "Usuarios activos")}
                </>
            )}

            {datosOriginales && datosOriginales.length > 0 && (
                <BotonFlotante
                    opciones={[
                        { texto: "Refrescar", icono: "🔄", accion: consultar }
                    ]}
                    data={datosParaExcel}
                    headers={columnasExcel}
                    nombreArchivo={`logs_${filtros.anio}_${filtros.mes}_${permisosSeleccionados.join("-")}`}
                />
            )}
        </div>
    );
};

export default EstadisticaLogs;